import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MainContent from '../components/MainContent';
import downloadReport from '../utils/downloadReport';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import {
  ADMISSIONS_PATH,
  PAGE_SIZE,
  admissionError,
  listParams,
  useAdmissions,
  useAdmissionsSummary,
  useBulkReplay,
} from '../hooks/useAdmissions';
import AdmissionDrawer from '../components/admissions/AdmissionDrawer';
import StatusBadge from '../components/admissions/StatusBadge';
import {
  CARD,
  OUTCOME_LABELS,
  STATUSES,
  STATUS_HELP,
  STATUS_LABELS,
  cohortLabel,
  dayEnd,
  dayStart,
  formatDateTime,
  reasonLabel,
} from '../components/admissions/admissionText';

const INPUT =
  'rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-claret-500 focus:outline-none focus:ring-1 focus:ring-claret-500';

function Unavailable({ error, onRetry }) {
  const status = error?.response?.status;
  return (
    <div className={`${CARD} p-8 text-center space-y-3`}>
      <p className="text-sm text-gray-600">
        {admissionError(error, "We couldn't load admissions.")}
      </p>
      {status !== 403 && status !== 404 && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400"
        >
          Retry
        </button>
      )}
    </div>
  );
}

function Chip({ active, onClick, label, count, tone, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
        active
          ? 'border-claret-500 bg-claret-500 text-white'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      {label}
      {count !== undefined && (
        <span
          className={`rounded-full px-1.5 text-[10px] font-semibold ${
            active ? 'bg-white/20' : tone || 'bg-gray-100 text-gray-600'
          }`}
        >
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}

// Failure reasons in range, each with "Show" and "Replay all".
function FailureReasons({ reasons, enabled, filters, onShow }) {
  const bulk = useBulkReplay();
  const [confirm, setConfirm] = useState(null);
  if (!reasons?.length) return null;

  const replayAll = (reason) =>
    bulk.mutate(
      { ...filters, status: 'failed', reason, allAttempts: false },
      {
        onSuccess: ({ replayed, skipped }) => {
          setConfirm(null);
          if (!replayed.length && !skipped.length) {
            toast('Nothing left to replay for this reason.');
          } else if (skipped.length) {
            toast.success(
              `Replaying ${replayed.length}; ${skipped.length} skipped (already replayed or not replayable).`
            );
          } else {
            toast.success(`Replaying ${replayed.length}.`);
          }
        },
        onError: (error) => {
          setConfirm(null);
          toast.error(admissionError(error, "We couldn't start the replays."));
        },
      }
    );

  return (
    <section className={`${CARD} p-4 space-y-3`}>
      <div>
        <h2 className="text-sm font-semibold text-gray-900">
          Why applicants weren&apos;t admitted
        </h2>
        <p className="text-xs text-gray-500">
          Fix the cause on forms or in the LMS first, then replay.
        </p>
      </div>
      <ul className="m-0 list-none divide-y divide-gray-100 p-0">
        {reasons.map((item) => (
          <li
            key={item.reason}
            className="flex flex-wrap items-center justify-between gap-3 py-2"
          >
            <div className="min-w-0">
              <p className="text-sm text-gray-900">
                {item.reason_text || item.reason}
              </p>
              <p className="text-xs text-gray-500">
                {item.count.toLocaleString()} applicant
                {item.count === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onShow(item.reason)}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Show
              </button>
              {enabled && confirm !== item.reason && (
                <button
                  type="button"
                  onClick={() => setConfirm(item.reason)}
                  disabled={bulk.isLoading}
                  className="inline-flex items-center gap-1 rounded-md border border-claret-500 px-3 py-1 text-xs text-claret-500 hover:bg-gray-50 disabled:opacity-60"
                >
                  <ReplayIcon sx={{ fontSize: 14 }} />
                  Replay all {item.count.toLocaleString()}
                </button>
              )}
              {enabled && confirm === item.reason && (
                <>
                  <button
                    type="button"
                    onClick={() => replayAll(item.reason)}
                    disabled={bulk.isLoading}
                    className="rounded-md bg-claret-500 px-3 py-1 text-xs text-white hover:bg-claret-400 disabled:opacity-60"
                  >
                    {bulk.isLoading ? 'Starting…' : 'Confirm replay'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    disabled={bulk.isLoading}
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatusCell({ admission }) {
  let note = null;
  if (admission.status === 'admitted') {
    note = OUTCOME_LABELS[admission.outcome];
  } else if (admission.status === 'failed') {
    note = reasonLabel(admission);
  } else if (admission.status === 'dead_lettered') {
    note = admission.last_error || STATUS_HELP.dead_lettered;
  }
  return (
    <div className="space-y-0.5">
      <StatusBadge status={admission.status} />
      {note && (
        <p className="max-w-xs truncate text-xs text-gray-500" title={note}>
          {note}
        </p>
      )}
    </div>
  );
}

function AdmissionsTable({ data, offset, onPage, onOpen, allAttempts }) {
  const rows = data?.admissions ?? [];
  const total = data?.total ?? 0;
  if (!total) {
    return (
      <div className={`${CARD} p-10 text-center space-y-1`}>
        <p className="text-sm text-gray-900">No admissions match</p>
        <p className="text-xs text-gray-500">
          Applicants accepted on forms appear here once the LMS receives them.
        </p>
      </div>
    );
  }
  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <th className="py-2 pl-5 pr-2 font-medium">Applicant</th>
              <th className="px-2 py-2 font-medium">Cohort</th>
              <th className="px-2 py-2 font-medium">Status</th>
              <th className="px-2 py-2 font-medium">Received</th>
              <th className="px-2 py-2 text-right font-medium">Attempts</th>
              <th className="py-2 pl-2 pr-5" aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {rows.map((admission) => (
              <tr
                key={admission.id}
                onClick={() => onOpen(admission.id)}
                className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
              >
                <td className="py-2.5 pl-5 pr-2">
                  {admission.applicant?.name || admission.applicant?.email ? (
                    <>
                      <span className="text-gray-900">
                        {admission.applicant.name || admission.applicant.email}
                      </span>
                      {admission.applicant.name &&
                        admission.applicant.email && (
                          <span className="block text-xs text-gray-500">
                            {admission.applicant.email}
                          </span>
                        )}
                    </>
                  ) : (
                    <span className="text-gray-500">Not fetched yet</span>
                  )}
                  {allAttempts && admission.replayed_from && (
                    <span className="block text-[11px] text-gray-400">
                      Replay
                    </span>
                  )}
                </td>
                <td className="px-2 py-2.5 text-gray-700">
                  {cohortLabel(admission.cohort)}
                </td>
                <td className="px-2 py-2.5">
                  <StatusCell admission={admission} />
                </td>
                <td className="whitespace-nowrap px-2 py-2.5 text-gray-700">
                  {formatDateTime(admission.received_at)}
                </td>
                <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                  {admission.attempts ?? '–'}
                </td>
                <td className="py-2.5 pl-2 pr-5 text-right text-gray-400">
                  <ChevronRightIcon fontSize="small" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
        <span>
          {(offset + 1).toLocaleString()}–
          {Math.min(offset + rows.length, total).toLocaleString()} of{' '}
          {total.toLocaleString()}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
            className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => onPage(offset + PAGE_SIZE)}
            className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

// Applicants accepted on forms and what happened when the LMS admitted them
// to their cohort. Filters live in the URL so the sidebar badge can link to
// "needs attention" and views can be shared.
export default function Admissions() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '';
  const reason = params.get('reason') || '';
  const cohortId = params.get('cohort') || '';
  const q = params.get('q') || '';
  const fromDay = params.get('from') || '';
  const toDay = params.get('to') || '';
  const allAttempts = params.get('all') === '1';
  const offset = Number(params.get('offset')) || 0;
  const openId = params.get('id') || '';
  const [search, setSearch] = useState(q);
  const [exporting, setExporting] = useState(false);

  const update = (changes, { keepOffset = false } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    if (!keepOffset) next.delete('offset');
    setParams(next, { replace: true });
  };

  // Search after typing pauses.
  useEffect(() => {
    if (search.trim() === q) return undefined;
    const timer = setTimeout(() => update({ q: search.trim() }), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const from = dayStart(fromDay);
  const to = dayEnd(toDay);
  const filters = { cohortId, status, reason, q, from, to, allAttempts };

  const summary = useAdmissionsSummary({ cohortId, from, to });
  const list = useAdmissions(filters, offset);
  const enabled = summary.data?.enabled !== false;
  const counts = summary.data?.counts ?? {};
  const cohorts = summary.data?.cohorts ?? [];

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadReport(`${ADMISSIONS_PATH}/export`, 'admissions.csv', {
        client: axiosInstancev2,
        params: listParams(filters),
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  const fatal = summary.error || (list.error && !list.data);
  const filtered = Boolean(
    status || reason || cohortId || q || fromDay || toDay || allAttempts
  );

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Admissions from forms
            </h1>
            <p className="text-xs text-gray-500">
              Applicants accepted on forms.powerlearnprojectafrica.org, and
              whether the LMS admitted them to their cohort.
              {summary.data?.last_received_at &&
                ` Last received ${formatDateTime(
                  summary.data.last_received_at
                )}.`}
            </p>
          </div>
          {!fatal && (
            <button
              type="button"
              onClick={exportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <DownloadIcon fontSize="small" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
        </header>

        {summary.data && !enabled && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Admissions from forms are switched off. New acceptances wait until
            they&apos;re switched back on, and replay is unavailable.
          </div>
        )}

        {(() => {
          if (summary.isLoading) {
            return (
              <div
                className={`${CARD} h-64 animate-pulse`}
                aria-label="Loading"
              />
            );
          }
          if (fatal) {
            return (
              <Unavailable
                error={summary.error || list.error}
                onRetry={() => {
                  summary.refetch();
                  list.refetch();
                }}
              />
            );
          }
          return (
            <>
              <section className={`${CARD} p-4 space-y-3`}>
                <div className="flex flex-wrap gap-2">
                  <Chip
                    label="All"
                    active={!status}
                    onClick={() => update({ status: '', reason: '' })}
                  />
                  <Chip
                    label="Needs attention"
                    count={summary.data?.needs_attention ?? 0}
                    tone="bg-red-50 text-red-700"
                    active={status === 'needs_attention'}
                    onClick={() =>
                      update({ status: 'needs_attention', reason: '' })
                    }
                  />
                  {STATUSES.map((key) => (
                    <Chip
                      key={key}
                      label={STATUS_LABELS[key]}
                      title={STATUS_HELP[key]}
                      count={counts[key] ?? 0}
                      active={status === key && !reason}
                      onClick={() => update({ status: key, reason: '' })}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    aria-label="Cohort"
                    value={cohortId}
                    onChange={(e) => update({ cohort: e.target.value })}
                    className={`${INPUT} max-w-xs`}
                  >
                    <option value="">All cohorts</option>
                    {cohorts.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohortLabel(cohort)} ({cohort.count.toLocaleString()}
                        {cohort.needs_attention
                          ? `, ${cohort.needs_attention} need attention`
                          : ''}
                        )
                      </option>
                    ))}
                  </select>
                  <input
                    type="search"
                    aria-label="Search applicants"
                    placeholder="Search name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`${INPUT} w-56`}
                  />
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    From
                    <input
                      type="date"
                      aria-label="From"
                      value={fromDay}
                      max={toDay || undefined}
                      onChange={(e) => update({ from: e.target.value })}
                      className={INPUT}
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    To
                    <input
                      type="date"
                      aria-label="To"
                      value={toDay}
                      min={fromDay || undefined}
                      onChange={(e) => update({ to: e.target.value })}
                      className={INPUT}
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={allAttempts}
                      onChange={(e) =>
                        update({ all: e.target.checked ? '1' : '' })
                      }
                    />
                    Show all attempts
                  </label>
                  {filtered && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setParams(openId ? { id: openId } : {}, {
                          replace: true,
                        });
                      }}
                      className="text-xs text-claret-500 hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
                {reason && (
                  <p className="text-xs text-gray-600">
                    Showing failures:{' '}
                    <span className="font-medium">
                      {summary.data?.failure_reasons?.find(
                        (r) => r.reason === reason
                      )?.reason_text || reason}
                    </span>{' '}
                    <button
                      type="button"
                      onClick={() => update({ reason: '' })}
                      className="text-claret-500 hover:underline"
                    >
                      Show all failures
                    </button>
                  </p>
                )}
              </section>

              <FailureReasons
                reasons={summary.data?.failure_reasons}
                enabled={enabled}
                filters={{ cohortId, from, to }}
                onShow={(key) => update({ status: 'failed', reason: key })}
              />

              {list.isLoading ? (
                <div
                  className={`${CARD} h-64 animate-pulse`}
                  aria-label="Loading"
                />
              ) : (
                <AdmissionsTable
                  data={list.data}
                  offset={offset}
                  allAttempts={allAttempts}
                  onPage={(next) =>
                    update(
                      { offset: next ? String(next) : '' },
                      {
                        keepOffset: true,
                      }
                    )
                  }
                  onOpen={(id) => update({ id }, { keepOffset: true })}
                />
              )}
            </>
          );
        })()}
      </div>

      <AdmissionDrawer
        id={openId}
        enabled={enabled}
        onOpen={(id) => update({ id }, { keepOffset: true })}
        onClose={() => update({ id: '' }, { keepOffset: true })}
      />
    </MainContent>
  );
}
